import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Card,
  Typography,
  Button,
  Tag,
  Spin,
  message,
  Space,
  Descriptions,
  Breadcrumb,
  Alert
} from 'antd';
import {
  FileTextOutlined,
  CalendarOutlined,
  UserOutlined,
  LinkOutlined,
  ExportOutlined,
  ArrowLeftOutlined,
  HomeOutlined
} from '@ant-design/icons';
import LandingLayout from './LandingLayoutRedesign';
import './NewsDetailsRedesign.css';

const { Title, Text, Paragraph } = Typography;

const NewsDetailsRedesign = () => {
  const { issuerName, newsId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [newsData, setNewsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get news data from location state or set from params
  useEffect(() => {
    if (location.state?.news) {
      setNewsData(location.state.news);
      setLoading(false);
    } else {
      // If no state, we could fetch the news data here if needed
      setError("News data not found");
      setLoading(false);
    }
  }, [location.state]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return dateStr;
    }
  };

  // Handle external link
  const handleExternalLink = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  // Handle back navigation
  const handleBack = () => {
    navigate(-1);
  };

  // Handle home navigation
  const handleHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <LandingLayout>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <Spin size="large" />
          <div style={{ marginTop: '1rem' }}>Loading news details...</div>
        </div>
      </LandingLayout>
    );
  }

  if (error || !newsData) {
    return (
      <LandingLayout>
        <div className="news-details-container">
          <div className="news-details-content">
            <Alert
              message="News Not Found"
              description="The requested news article could not be found."
              type="error"
              showIcon
            />
          </div>
        </div>
      </LandingLayout>
    );
  }

  return (
    <LandingLayout>
      <div className="news-details-container">
        {/* Breadcrumb Section */}
        <div className="breadcrumb-section">
          <Breadcrumb
            items={[
              {
                title: (
                  <Button
                    type="text"
                    icon={<HomeOutlined />}
                    onClick={handleHome}
                    style={{ padding: 0, height: 'auto' }}
                  >
                    Home
                  </Button>
                ),
              },
              {
                title: (
                  <Button
                    type="text"
                    onClick={() => navigate(-1)}
                    style={{ padding: 0, height: 'auto' }}
                  >
                    {issuerName}
                  </Button>
                ),
              },
              {
                title: 'News Details',
              },
            ]}
          />
        </div>

        {/* Content Section */}
        <div className="news-details-content">
          <Card className="news-details-card">
            {/* News Header */}
            <div className="news-details-news-header">
              <div className="news-details-source-section">
                <Tag color="blue" size="large">
                  {newsData.source || 'Unknown Source'}
                </Tag>
                <Tag color="green" size="large">
                  {newsData.company || 'N/A'}
                </Tag>
              </div>
              <div className="news-details-date-section">
                <Space>
                  <CalendarOutlined />
                  <Text type="secondary">
                    {formatDate(newsData.publishedDate)}
                  </Text>
                </Space>
              </div>
            </div>

            {/* News Title */}
            <Title level={3} className="news-details-news-title">
              {newsData.title || 'N/A'}
            </Title>

            {/* News Description */}
            <Paragraph className="news-details-description">
              {newsData.description || 'N/A'}
            </Paragraph>

            {/* News Details */}
            <div className="news-details-info">
              <Descriptions column={2} bordered>
                <Descriptions.Item label="Subtitle">
                  {newsData.subtitle || 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label="Author">
                  <Space>
                    <UserOutlined />
                    {newsData.author || 'N/A'}
                  </Space>
                </Descriptions.Item>

                <Descriptions.Item label="Time">
                  {newsData.time || 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label="Topics">
                  {newsData.topics || 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label="Scraped At">
                  {newsData.scrapedAt ? new Date(newsData.scrapedAt).toLocaleString() : 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label="Created At">
                  {newsData.createdAt ? new Date(newsData.createdAt).toLocaleString() : 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label="Updated At">
                  {newsData.updatedAt ? new Date(newsData.updatedAt).toLocaleString() : 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            </div>

            {/* Action Buttons */}
            <div className="news-details-actions">
              <Space>
                <Button
                  type="primary"
                  icon={<ExportOutlined />}
                  onClick={() => handleExternalLink(newsData.url)}
                  disabled={!newsData.url}
                >
                  Open Source
                </Button>
                <Button onClick={handleBack}>
                  Back to News
                </Button>
              </Space>
            </div>
          </Card>
        </div>
      </div>
    </LandingLayout>
  );
};

export default NewsDetailsRedesign;
